import { motion } from 'framer-motion';
import { FileText, Receipt, Languages, Link2, LayoutDashboard, ClipboardList, UserCheck } from 'lucide-react';

const features = [
  {
    id: 'soap',
    title: 'Streaming SOAP Notes',
    description:
      'Watch your note appear word-by-word as Gemini 2.5 Pro generates it. Subjective, Objective, Assessment, and Plan — structured and typically ready within 30 seconds.',
    size: 'large', // col-span-2
    icon: <FileText className="h-6 w-6" />,
    accent: 'bg-primary-50 text-primary-600',
    preview: (
      <div className="mt-4 rounded-lg bg-white border border-gray-100 p-4 text-sm space-y-3 shadow-sm">
        {[
          { label: 'S', color: 'bg-blue-100 text-blue-700', text: 'Patient presents with persistent dry cough for 3 weeks, no fever...' },
          { label: 'O', color: 'bg-purple-100 text-purple-700', text: 'BP 128/82, HR 74 bpm, RR 16, SpO₂ 98% on room air...' },
          { label: 'A', color: 'bg-amber-100 text-amber-700', text: 'ACE inhibitor-induced cough. No signs of infection or cardiac...' },
          { label: 'P', color: 'bg-green-100 text-green-700', text: 'Switch to ARB. Follow up in 4 weeks. Patient educated on...' },
        ].map(({ label, color, text }) => (
          <div key={label} className="flex gap-2.5 items-start">
            <span className={`flex-shrink-0 h-5 w-5 rounded text-xs font-bold flex items-center justify-center ${color}`}>{label}</span>
            <span className="text-gray-600 text-xs leading-relaxed">{text}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'billing',
    title: 'OHIP Billing Codes',
    description:
      'Automatically surfaces OHIP service codes and premium diagnostic multipliers — including E078A for chronic care and 1.5× codes for ADHD, asthma, and diabetes.',
    size: 'normal',
    icon: <Receipt className="h-6 w-6" />,
    accent: 'bg-primary-50 text-primary-600',
    preview: (
      <div className="mt-4 rounded-lg bg-white border border-gray-100 p-4 shadow-sm space-y-2">
        {[
          { code: 'A265A', desc: 'General Assessment', badge: 'Suggested', color: 'bg-green-100 text-green-700' },
          { code: 'E078A', desc: 'Chronic Care Mgmt', badge: 'Premium ×1.5', color: 'bg-amber-100 text-amber-700' },
          { code: 'K030A', desc: 'Counseling (K-code)', badge: 'Suggested', color: 'bg-green-100 text-green-700' },
        ].map(({ code, desc, badge, color }) => (
          <div key={code} className="flex items-center justify-between text-xs">
            <div>
              <span className="font-mono font-semibold text-gray-900">{code}</span>
              <span className="ml-2 text-gray-500">{desc}</span>
            </div>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>{badge}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: 'languages',
    title: 'Multilingual Support',
    description:
      'Patients speak in English, French, Spanish, Hindi, Urdu, Punjabi, or Arabic — Aura-Chart transcribes and outputs a clean English SOAP note every time.',
    size: 'normal',
    icon: <Languages className="h-6 w-6" />,
    accent: 'bg-primary-50 text-primary-600',
    preview: (
      <div className="mt-4 flex flex-wrap gap-2">
        {['English', 'French CA', 'Spanish', 'Hindi', 'Urdu', 'Punjabi', 'Arabic', 'Auto-detect'].map((lang) => (
          <span key={lang} className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-xs font-medium">
            {lang}
          </span>
        ))}
      </div>
    ),
  },
  {
    id: 'emr',
    title: 'Works With Any EMR',
    description:
      'Aura-Chart floats over your existing EMR as a Chrome extension. Copy your note and paste it directly — no integration, no IT tickets, no disruption to your workflow.',
    size: 'large',
    icon: <Link2 className="h-6 w-6" />,
    accent: 'bg-primary-50 text-primary-600',
    preview: (
      <div className="mt-4 flex flex-wrap gap-2">
        {['Accuro', 'OSCAR Pro', 'TELUS CHR', 'Jane App', 'PS Suite', 'JUNO Health', 'Any web EMR'].map((emr) => (
          <span key={emr} className="px-3 py-1.5 rounded-lg bg-indigo-50 text-indigo-700 text-xs font-medium border border-indigo-100">
            {emr}
          </span>
        ))}
      </div>
    ),
  },
  {
    id: 'sessions',
    title: '5 Concurrent Sessions',
    description:
      'Manage up to five patient sessions simultaneously without losing state. Switch between patients mid-clinic — each session is isolated and persists through browser refreshes.',
    size: 'normal',
    icon: <LayoutDashboard className="h-6 w-6" />,
    accent: 'bg-primary-50 text-primary-600',
    preview: (
      <div className="mt-4 flex gap-2">
        {['1 ●', '2 ●', '3 ●', '4 ●', '5 ●'].map((tab, i) => (
          <span
            key={tab}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border ${
              i === 0
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white text-gray-600 border-gray-200'
            }`}
          >
            {tab}
          </span>
        ))}
      </div>
    ),
  },
  {
    id: 'secondary',
    title: 'Referrals, Tasks & Letters',
    description:
      'Generate specialist referral letters and extract follow-up task lists directly from your SOAP note. All in the same workflow, no extra steps.',
    size: 'normal',
    icon: <ClipboardList className="h-6 w-6" />,
    accent: 'bg-primary-50 text-primary-600',
    preview: (
      <div className="mt-4 space-y-2 text-xs">
        {[
          '✓ Refer to Cardiology — arrange echo',
          '✓ Recheck BP in 4 weeks',
          '✓ Renew metformin prescription',
          '✓ Patient education: low-sodium diet',
        ].map((task) => (
          <p key={task} className="text-gray-500">{task}</p>
        ))}
      </div>
    ),
  },
  {
    id: 'patient-summary',
    title: 'Patient Visit Summary',
    description:
      'Generate a plain-language summary the patient takes home — what was discussed, what to watch for, next steps. No other Canadian AI scribe offers this.',
    size: 'normal',
    icon: <UserCheck className="h-6 w-6" />,
    accent: 'bg-primary-50 text-primary-600',
    preview: (
      <div className="mt-4 rounded-lg bg-white border border-gray-100 p-4 shadow-sm text-xs space-y-2">
        <p className="font-semibold text-gray-700">Visit Summary — June 12, 2026</p>
        <p className="text-gray-500 leading-relaxed">Your doctor discussed your blood pressure today. You'll start a new medication — take it once daily with food. Return in 4 weeks for a follow-up check.</p>
        <div className="flex gap-2 mt-3">
          <span className="px-2.5 py-1 rounded-full bg-rose-50 text-rose-600 font-medium text-xs">Copy for Patient</span>
          <span className="px-2.5 py-1 rounded-full bg-gray-50 text-gray-500 font-medium text-xs">Share / Print</span>
        </div>
      </div>
    ),
  },
];

export default function Features() {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="section-label">Features</p>
          <h2 className="mt-3 section-heading">Everything a clinician needs, nothing they don't</h2>
        </motion.div>

        {/* Bento grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-auto">
          {features.map((feature, i) => (
            <motion.div
              key={feature.id}
              className={`bg-gray-50 rounded-2xl p-6 border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200 ${
                feature.size === 'large' ? 'lg:col-span-2' : ''
              }`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
            >
              <div className={`inline-flex items-center justify-center h-11 w-11 rounded-xl mb-4 ${feature.accent}`}>
                {feature.icon}
              </div>
              <h3 className="text-lg font-semibold text-gray-900">{feature.title}</h3>
              <p className="mt-2 text-sm text-gray-500 leading-relaxed">{feature.description}</p>
              {feature.preview}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
